/**
 * DigoAvatar3D — real, rigged 3D avatar with actual lip-sync, via
 * @met4citizen/talkinghead (MIT, wraps three.js). Docs/DIGO_VOICE_ADMIN_ASSISTANT_PLAN.md
 * §7's photo-avatar-with-a-pulsing-ring was an honest stand-in for "he's
 * talking" — this replaces that stand-in with the real thing: word-timed
 * viseme animation on a rigged glTF head.
 *
 * Avatar asset: `frontend/public/digo-avatar/mpfb.glb` — the CC0-licensed
 * ("public domain equivalent") sample avatar bundled with the TalkingHead
 * project itself (made with Blender + the MPFB extension), confirmed via
 * https://github.com/met4citizen/TalkingHead's own README credits before
 * using it — the library's OTHER bundled sample avatars (Ready Player Me,
 * Avaturn, AvatarSDK, VRoid) are explicitly non-commercial-only licensed,
 * so this is the only one of the bundled options safe to ship in a real
 * product. It's a placeholder like the photo slot was: swap the file for
 * a custom Ready Player Me/MPFB avatar later, same "drop a file in" idea.
 *
 * 2026-08-19: real TTS audio now exists (digo_engine.py synthesizes the
 * reply through the same voice_note_service the calling pipeline uses, see
 * that file's own note).
 *
 * 2026-08-19 (later same day): this component no longer plays the real
 * voice at all — it's lip-sync-only now. Originally speak() took the
 * decoded AudioBuffer and handed it to TalkingHead's own speakAudio(),
 * which plays it through the library's own internal AudioContext/queue.
 * Live testing showed that path is fragile in a way that fails silently:
 * if the library's audioCtx.resume() doesn't succeed (e.g. the browser's
 * autoplay-suspend policy), talkinghead.mjs retries forever with only a
 * bare console.log, never an error DigoLauncher.tsx could catch or react
 * to — and it only ever showed up on the SECOND reply onward, because the
 * first reply typically arrives before this 3D avatar (a ~36MB lazy-loaded
 * model) has finished loading, so it plays through DigoLauncher's own
 * simple, proven-reliable AudioContext path instead. Root cause of "voice
 * worked once then went silent."
 *
 * Fix: DigoLauncher.tsx now ALWAYS plays the real decoded audio itself,
 * through that same simple path, regardless of whether the 3D avatar is
 * ready — voice no longer depends on this component at all. This
 * component's speak() now only drives the mouth: it takes an optional
 * duration hint (the real audio's length in ms, so lip timing still lines
 * up) and animates against a SILENT buffer of that length via
 * speakAudio(), same as the original "no audio this turn" fallback below
 * already did. A stuck/failed lip-sync animation can no longer take the
 * actual voice down with it.
 *
 * Also sets the renderer's clear color to transparent right after
 * construction — the library creates its WebGLRenderer with `alpha: true`
 * but never calls setClearColor itself (confirmed by reading
 * modules/talkinghead.mjs directly), so three.js's own default (opaque
 * black) would otherwise render as a black box behind the avatar instead of
 * showing the page through it.
 *
 * Lazy-loaded: three.js + talkinghead + the ~36MB model only load once the
 * admin actually opens Digo (see DigoLauncher.tsx), never on page load.
 */
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

const AVATAR_GLB_SRC = '/digo-avatar/mpfb.glb';

export type DigoAvatar3DHandle = {
  speak: (text: string, durationMsHint?: number) => void;
};

type Props = {
  onReady?: () => void;
  onError?: (err: unknown) => void;
};

const DigoAvatar3D = forwardRef<DigoAvatar3DHandle, Props>(({ onReady, onError }, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const headRef = useRef<any>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const [ready, setReady] = useState(false);
  // Set after showAvatar() from head.morphs.length (talkinghead.mjs builds
  // this array only from meshes that have BOTH morph target data AND a
  // morphTargetDictionary — the latter requires the glTF's
  // extras.targetNames to be present, which not every avatar exporter
  // writes even when the raw blend-shape geometry itself is there). If this
  // avatar has none, calling head.speakAudio() throws inside resetLips()
  // (talkinghead.mjs's morphTargetDictionary['viseme_'+x] on an undefined
  // dictionary) before it ever reaches the audio-playback step later in the
  // same function — so a naming-only problem in the model file silently
  // takes sound down with it, not just lip-sync. See DigoLauncher.tsx's
  // console.warn('[Digo] speakAudio failed', ...) if this fires.
  const hasUsableMorphsRef = useRef(true);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const mod = await import('@met4citizen/talkinghead');
        // talkinghead.mjs's own lipsyncGetProcessor() builds its module path
        // by string concatenation ('./' + 'lipsync-' + lang + '.mjs') and
        // calls import() on the resulting variable — webpack can't statically
        // analyze a dynamically-built import path, so it never bundles that
        // chunk. At runtime the import silently rejects (unhandled promise,
        // "Cannot find module './lipsync-en.mjs'") and this.lipsync['en']
        // never gets set, so the first real lipsyncPreProcessText() call
        // later throws reading .preProcessText off undefined — this is what
        // produced the "[Digo] speakAudio failed" warning. Importing the
        // same file here with a static, literal path lets webpack bundle it
        // properly; we hand the instance to the library ourselves below
        // instead of letting its own broken self-import run.
        const lipsyncEnMod = await import('@met4citizen/talkinghead/modules/lipsync-en.mjs');
        if (cancelled || !containerRef.current) return;

        const head = new mod.TalkingHead(containerRef.current, {
          ttsEndpoint: null,
          // Left empty deliberately — see the lipsyncEnMod import above.
          // Populated manually right after construction instead.
          lipsyncModules: [],
          // 'upper' (chest-up) instead of 'head' (tight face crop) gives the
          // idle sway/breathing motion room to move without the head edges
          // clipping the frame — the 'head' framing was tight enough that
          // any sway pushed the face past the canvas bounds.
          cameraView: 'upper',
          cameraDistance: 0.4,
          avatarMood: 'neutral',
        });

        // Manually populate what the library's own broken self-import would
        // have set — this.lipsync is a plain public object (talkinghead.mjs
        // initializes it to {} in the constructor), so this is safe to set
        // directly from outside. Must happen before any speakAudio() call.
        (head as any).lipsync.en = new lipsyncEnMod.LipsyncEn();

        // Transparent background — see file header note. Must run before
        // showAvatar's first render so there's no opaque-black flash.
        head.renderer.setClearColor(0x000000, 0);

        // 2026-08-19: NO headRotateX baseline passed here anymore (used to
        // be a hardcoded `{ headRotateX: 0.1 }` guess — confirmed
        // insufficient, the avatar still looked down). Left unset
        // deliberately so the diagnostic read right below sees this
        // avatar's true, unmodified rest-pose tilt instead of an
        // already-corrected value.
        await head.showAvatar({
          url: AVATAR_GLB_SRC,
          body: 'F',
          lipsyncLang: 'en',
          avatarMood: 'neutral',
        });

        if (cancelled) return;

        // Self-correcting head-tilt fix, replacing the old hardcoded guess.
        // Root cause (confirmed by reading talkinghead.mjs directly, not
        // guessed): this avatar's GLB rest pose has the Head bone pitched
        // down. talkinghead.mjs's updatePoseDelta() applies headRotateX as
        // a LOCAL EULER-X ROTATION IN RADIANS on top of that rest pose each
        // frame (`e.set(d.x,d.y,d.z); q.setFromEuler(e); o.multiply(q)`,
        // where d.x comes straight from the headRotateX morph target's
        // applied value — see the 'headRotateX' case in
        // updateMorphTargets()'s switch). Right here, immediately after
        // showAvatar() resolves and before any animation frame has run,
        // the Head bone's quaternion is still exactly the file's raw rest
        // pose (setupAvatar's own setup captures poseBase from this same
        // bone the first time an avatar loads, then copies it back onto
        // the bone — confirmed by reading that code too) — so reading its
        // Euler x here gives the real tilt in radians, not an estimate.
        // Since a rotation and its exact negative on the SAME axis cancel
        // regardless of multiplication order (q(θ)·q(-θ) = identity),
        // setting the headRotateX baseline to -restRotationX brings the
        // head to level precisely, for whatever this avatar's actual tilt
        // turns out to be — no more "retune by eye" if the model file ever
        // changes.
        try {
          const anyHead = head as any;
          const headBone = anyHead.armature?.getObjectByName?.('Head');
          console.info('[Digo] camera position:', anyHead.camera?.position?.toArray?.());
          console.info('[Digo] controls.target:', anyHead.controls?.target?.toArray?.());
          if (headBone) {
            const restRotationX = headBone.rotation.x;
            console.info('[Digo] Head bone rest-pose local rotation (x,y,z radians):', [restRotationX, headBone.rotation.y, headBone.rotation.z]);
            if (Number.isFinite(restRotationX) && Math.abs(restRotationX) > 0.001) {
              anyHead.setBaselineValue?.('headRotateX', -restRotationX);
              console.info(`[Digo] head-tilt auto-correction applied: headRotateX baseline set to ${(-restRotationX).toFixed(4)} rad`);
            }
          } else {
            console.warn('[Digo] could not find a bone named "Head" on this avatar\'s armature — camera framing/head-tilt logic may be targeting the wrong node.');
          }
        } catch (diagErr) {
          console.warn('[Digo] head-tilt auto-correction failed (non-fatal, avatar keeps its raw rest-pose tilt)', diagErr);
        }

        // head.morphs exists at runtime (talkinghead.mjs sets it as a plain
        // field) but isn't in the package's .d.ts, hence the `any` cast.
        const morphCount = Array.isArray((head as any).morphs) ? (head as any).morphs.length : 0;
        hasUsableMorphsRef.current = morphCount > 0;
        if (morphCount > 0) {
          console.info(`[Digo] 3D avatar loaded with ${morphCount} named morph-target mesh(es) — lip-sync available.`);
        } else {
          console.warn(
            '[Digo] 3D avatar loaded but has no usable morph targets (head.morphs is empty). ' +
            'The model likely has raw blend-shape geometry that isn\'t labeled via glTF ' +
            'extras.targetNames, so three.js can\'t build a name lookup for them. ' +
            'Falling back to plain audio playback with no lip-sync for this avatar — ' +
            're-export it with named ARKit/Oculus-viseme morph targets to fix (e.g. Ready ' +
            'Player Me\'s ?morphTargets=ARKit,Oculus Visemes export option) rather than the ' +
            'current file.'
          );
        }

        headRef.current = head;
        setReady(true);
        onReady?.();
      } catch (err) {
        // WebGL unsupported, the model failed to load, or the library threw —
        // DigoLauncher falls back to the photo avatar on this callback.
        console.warn('[Digo] 3D avatar failed to load, falling back to photo avatar', err);
        onError?.(err);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useImperativeHandle(ref, () => ({
    speak: (text: string, durationMsHint?: number) => {
      const head = headRef.current;
      const trimmed = text.trim();
      // No named morph targets on this avatar (see the head.morphs check in
      // the load effect above) — nothing to animate, and DigoLauncher.tsx
      // already plays the real voice itself regardless of this component,
      // so there's nothing lost by skipping entirely.
      if (!head || !trimmed || !hasUsableMorphsRef.current) return;

      try {
        const words = trimmed.split(/\s+/);
        // Rough per-word duration estimate — short words ~200ms, longer
        // words up to ~420ms. Word BOUNDARIES are still only an estimate
        // either way (no real per-word alignment data from synthesis), but
        // when a real duration hint is given, the TOTAL is rescaled to it
        // so the mouth stops moving when the (separately-played) voice
        // does, rather than drifting from the real playback length.
        const rawDurations = words.map((w) => Math.max(120, Math.min(420, 80 + w.length * 40)));
        const rawTotal = rawDurations.reduce((a, b) => a + b, 0) || 1;
        const targetTotalMs = durationMsHint ?? rawTotal;
        const scale = targetTotalMs / rawTotal;

        const wtimes: number[] = [];
        const wdurations: number[] = [];
        let t = 0;
        for (const raw of rawDurations) {
          const dur = raw * scale;
          wtimes.push(t);
          wdurations.push(dur);
          t += dur;
        }

        // Always a SILENT buffer — the real voice is played separately and
        // reliably by DigoLauncher.tsx now (see file header note). This is
        // purely a mouth-timing track handed to speakAudio(), sized to the
        // real audio's duration when known.
        const AudioCtx = (window as any).AudioContext || (window as any).webkitAudioContext;
        const ctx: AudioContext = audioCtxRef.current ?? (audioCtxRef.current = new AudioCtx());
        const totalSec = Math.max(0.3, t / 1000);
        const audio = ctx.createBuffer(1, Math.ceil(ctx.sampleRate * totalSec), ctx.sampleRate);

        head.speakAudio({ audio, words, wtimes, wdurations }, { lipsyncLang: 'en' });
      } catch (err) {
        // Best-effort visual only — the real voice already played (or is
        // playing) independently of this, so a failure here is silent lips,
        // never silent audio.
        console.warn('[Digo] lip-sync speakAudio failed (voice is unaffected)', err);
      }
    },
  }));

  return <div ref={containerRef} className="digo-avatar-3d-canvas" data-ready={ready} />;
});

DigoAvatar3D.displayName = 'DigoAvatar3D';

export default DigoAvatar3D;
